import * as turf from '@turf/turf';
import moment from 'moment';
import { projectDistance } from './projectionMethods';
import { EPOCH } from '../../../../common/utils';

const INTERNALS = 'cassie:internals';

/**
 * @function applyRollingSmooth
 * @description Applies a spatial rolling average (over adjacent transects) to each transect's
 * LRR (derived from `slope * 365`) and EPR rates, injecting the results as `smoothed_lrr`/
 * `smoothed_epr` properties.
 * @param {Array} features - Array of GeoJSON transect features.
 * @param {Number} [windowSize=3] - Number of adjacent transects (trailing window) to average over.
 * @param {Number} [minPeriods=2] - Minimum non-null values required to produce a result.
 * @see {@link rollingMean}
 * **Called by:
 * @see {performShoreRAnalysis} - ShoreR module's saga.
 * @memberof module:ShoreR
 * @returns {Array} New features array with `smoothed_lrr` and `smoothed_epr` injected into properties.
 */
export const applyRollingSmooth = (features, windowSize = 3, minPeriods = 2) => {
  /**
   * @function rollingMean
   * @description Computes a trailing rolling mean over a numeric series, skipping null/NaN
   * values and requiring at least `minP` valid values in the window to produce a result.
   * @param {Array<Number|null>} values - Series to average.
   * @param {Number} win - Trailing window size.
   * @param {Number} minP - Minimum valid values required.
   * **Called by:
   * @see {applyRollingSmooth}
   * @memberof module:ShoreR
   * @returns {Array<Number|null>} Rolling mean per index (null where insufficient data).
   */
  const rollingMean = (values, win, minP) => {
    const result = new Array(values.length).fill(null);
    for (let i = 0; i < values.length; i++) {
      const start = Math.max(0, i - win + 1);
      const window = values.slice(start, i + 1);
      const valid = window.filter((v) => v != null && !isNaN(v));
      if (valid.length >= minP) {
        result[i] = valid.reduce((sum, v) => sum + v, 0) / valid.length;
      }
    }
    return result;
  };

  const lrrValues = features.map((f) => {
    const slope = f.properties?.slope;
    return slope != null ? slope * 365 : null;
  });
  const eprValues = features.map((f) => f.properties?.epr ?? null);

  const smoothedLrr = rollingMean(lrrValues, windowSize, minPeriods);
  const smoothedEpr = rollingMean(eprValues, windowSize, minPeriods);

  return features.map((feature, i) => ({
    ...feature,
    properties: {
      ...feature.properties,
      smoothed_lrr: smoothedLrr[i],
      smoothed_epr: smoothedEpr[i],
    },
  }));
};

/**
 * @function computeProjections
 * @description Computes projected future shoreline points for each requested horizon.
 * 1. Builds reference lines from `referenceFeature` (if given) to intersect each transect against.
 * 2. For each transect and horizon, delegates the distance projection to `projectionMethods.js`,
 *    then determines the projection's origin point — either the transect's latest shoreline
 *    intersection, or its intersection with the reference line (per `referenceMode`).
 * 3. Clamps the projected distance to the transect's physical extent, then computes the
 *    projected point via a seaward/landward bearing from the origin.
 * @param {Object} transectData - GeoJSON FeatureCollection from results.getInfo().
 * @param {Array<Number>} horizons - Projection horizons, in years (e.g. [10, 20, 30]).
 * @param {String} method - Projection method key (e.g. "smoothed_linear_regression").
 * @param {Object} referenceFeature - Reference shoreline feature to intersect transects against.
 * @param {String} referenceMode - "latest" to always use the latest shoreline intersection, otherwise
 * the reference-line intersection is used when available.
 * @param {Number} [raOffset=0] - Relative accretion offset subtracted from each projected distance.
 * @see {@link projectDistance}
 * **Called by:
 * @see {ShoreRAnalysisSteps}
 * @memberof module:ShoreR
 * @returns {Array<{horizon: Number, points: Array<{id: *, lat: Number, lng: Number}>}>} Projected points per horizon.
 */
export const computeProjections = (transectData, horizons, method, referenceFeature, referenceMode, raOffset = 0) => {
  const features = Array.isArray(transectData) ? transectData : transectData?.features;
  if (!features?.length) return [];

  const refLines = referenceFeature?.geometry
    ? referenceFeature.geometry.type === 'MultiLineString'
      ? referenceFeature.geometry.coordinates.map((c) => turf.lineString(c))
      : [turf.lineString(referenceFeature.geometry.coordinates)]
    : null;

  return horizons.map((horizon) => {
    const points = [];

    features.forEach((feature) => {
      const rawDistance = projectDistance(method, feature.properties, horizon);
      if (rawDistance == null) return;
      const projectedDistance = rawDistance - raOffset;

      const coords = feature.geometry?.coordinates;
      if (!coords || coords.length < 2) return;

      const start = coords[0];
      const end = coords[1];

      const { latestIntersectionLng, latestIntersectionLat } = feature.properties;

      let originCoord;
      if (referenceMode === 'latest' || !refLines) {
        if (latestIntersectionLng == null || latestIntersectionLat == null) return;
        originCoord = [latestIntersectionLng, latestIntersectionLat];
      } else {
        const transectLine = turf.lineString([start, end]);
        let intersection = null;
        for (const refLine of refLines) {
          const result = turf.lineIntersect(transectLine, refLine);
          if (result.features.length > 0) {
            intersection = result.features[0].geometry.coordinates;
            break;
          }
        }
        if (!intersection) return;
        originCoord = intersection;
      }

      const midpoint = turf.midpoint(turf.point(start), turf.point(end));
      const midCoord = midpoint.geometry.coordinates;

      const seawardBearing =
        latestIntersectionLng != null && latestIntersectionLat != null
          ? turf.bearing(turf.point(midCoord), turf.point([latestIntersectionLng, latestIntersectionLat]))
          : turf.bearing(turf.point(midCoord), turf.point(start));

      const halfExtent = turf.distance(turf.point(start), turf.point(end), { units: 'meters' }) / 2;
      const clamped = Math.max(-halfExtent, Math.min(projectedDistance, halfExtent));

      const absDist = Math.abs(clamped);
      const bearing = clamped >= 0 ? seawardBearing : seawardBearing + 180;

      const projectedPoint = turf.destination(turf.point(originCoord), absDist / 1000, bearing);

      const [lng, lat] = projectedPoint.geometry.coordinates;
      points.push({ id: feature.id, lat, lng });
    });

    return { horizon, points };
  });
};

/**
 * @function readFileAndGetCoordinates
 * @description Parses KML file content and extracts one coordinate array per
 * `<coordinates>...</coordinates>` block (e.g. one per baseline or transect line).
 * @param {String} fileContent - Raw text content of a KML file.
 * @param {Function} t - Translation function, used for the error message.
 * @param {Function} [notify] - How to tell the user, so a caller can report through the app's own
 *  notice instead of a blocking dialog.
 * **Called by:
 * @see {BaselineDefinitionCard}
 * @see {TransectDefinitionCard}
 * @memberof module:ShoreR
 * @returns {Array<Array>|undefined} One coordinate array per block (each with 2+ points), or
 * undefined if the file has no `<coordinates>` tag.
 */
export const readFileAndGetCoordinates = (fileContent, t, notify = window.alert) => {
  if (fileContent.indexOf('<coordinates>') === -1) {
    notify(t('forms.csqueeze.2.errorMessageUnableInterpretKml'));
    return;
  }
  const coordinatesBlocks = fileContent.split('<coordinates>');
  coordinatesBlocks.shift();
  const allObj = [];
  coordinatesBlocks.forEach((block) => {
    let coordinatesString = block.split('</coordinates>')[0];
    let coordinatesArrayStrings = coordinatesString.split(/\s+/).map((el) => el.trim());
    let coordinatesArray = [];
    coordinatesArrayStrings.forEach((el) => {
      if (el.trim() !== '') {
        let coordinatesValues = el.split(',');
        coordinatesValues = coordinatesValues.map((el) => parseFloat(el));
        if (!isNaN(coordinatesValues[0]) && !isNaN(coordinatesValues[1])) {
          coordinatesArray.push([coordinatesValues[0], coordinatesValues[1]]);
        }
      }
    });
    if (coordinatesArray.length >= 2) allObj.push(coordinatesArray);
  });
  return allObj;
};

/**
 * @function buildMeanShorelineFeature
 * @description Builds a synthetic "mean shoreline" LineString by shifting each point of the
 * latest shoreline polyline toward its nearest transect's `(meanLastYear − lastDistance)` offset,
 * where `meanLastYear` is that transect's average distance over the trailing `windowDays` window
 * (default: last 365 days from the most recent observation across all transects). This preserves
 * the latest shoreline's shape/density while representing a more representative recent position.
 * @param {Object} transectData - GeoJSON FeatureCollection of transects with regression histories.
 * @param {Object} shorelinesGeoJSON - GeoJSON FeatureCollection of shorelines.
 * @param {Number} [windowDays=365] - Trailing window, in days, used to compute each transect's mean.
 * **Called by:
 * @see {ShoreRAnalysisSteps}
 * @memberof module:ShoreR
 * @returns {Object|null} GeoJSON Feature (LineString), or null if there isn't enough data.
 */
export const buildMeanShorelineFeature = (transectData, shorelinesGeoJSON, windowDays = 365) => {
  const features = Array.isArray(transectData) ? transectData : transectData?.features;
  if (!features?.length) return null;

  const shorelineFeats = shorelinesGeoJSON?.features;
  if (!shorelineFeats?.length) return null;

  let maxDay = -Infinity;
  features.forEach((f) => {
    const reg = f.properties?.[INTERNALS]?.regression;
    if (!reg?.length) return;
    reg.forEach(([day]) => {
      if (day != null && day > maxDay) maxDay = day;
    });
  });
  if (!Number.isFinite(maxDay)) return null;

  const cutoff = maxDay - windowDays;

  const transectInfo = features.map((feature) => {
    const coords = feature.geometry?.coordinates;
    const props = feature.properties ?? {};
    const start = coords?.[0];
    const end = coords?.[1];
    if (!start || !end) return null;

    const midpoint = turf.midpoint(turf.point(start), turf.point(end));
    const midCoord = midpoint.geometry.coordinates;

    const { latestIntersectionLng, latestIntersectionLat } = props;
    const seawardBearing =
      latestIntersectionLng != null && latestIntersectionLat != null
        ? turf.bearing(turf.point(midCoord), turf.point([latestIntersectionLng, latestIntersectionLat]))
        : turf.bearing(turf.point(midCoord), turf.point(end));

    const reg = props[INTERNALS]?.regression ?? [];
    const lastDistance = reg.length ? reg[reg.length - 1][1] : 0;

    const recent = reg.filter(([day]) => day != null && day >= cutoff);
    const mean = recent.length ? recent.reduce((sum, [, dist]) => sum + dist, 0) / recent.length : null;

    return { midCoord, seawardBearing, lastDistance, mean };
  });

  const latestShoreline = shorelineFeats[shorelineFeats.length - 1];
  let coordinates = latestShoreline.geometry?.coordinates;
  if (!coordinates?.length) return null;
  if (latestShoreline.geometry.type === 'MultiLineString') {
    coordinates = coordinates.flat();
  }

  const points = [];
  coordinates.forEach(([lng, lat]) => {
    let minDist = Infinity;
    let nearest = null;
    transectInfo.forEach((info) => {
      if (!info) return;
      const dx = lng - info.midCoord[0];
      const dy = lat - info.midCoord[1];
      const d = dx * dx + dy * dy;
      if (d < minDist) {
        minDist = d;
        nearest = info;
      }
    });
    if (!nearest) return;

    if (nearest.mean == null) {
      points.push([lng, lat]);
      return;
    }

    const shift = nearest.mean - nearest.lastDistance;
    const absShift = Math.abs(shift);
    const bearing = shift >= 0 ? nearest.seawardBearing : nearest.seawardBearing + 180;

    const shifted = turf.destination(turf.point([lng, lat]), absShift / 1000, bearing);
    points.push(shifted.geometry.coordinates);
  });

  if (points.length < 2) return null;

  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: points },
    properties: { synthetic: true, windowDays },
  };
};

/**
 * @function computeMedian
 * @description Computes the median of a numeric array.
 * @param {Array<Number>} values - Values to compute the median of.
 * **Called by:
 * @see {detectOutlierShorelineDates}
 * @memberof module:ShoreR
 * @returns {Number} The median value, or NaN if the array is empty.
 */
const computeMedian = (values) => {
  if (!values.length) return NaN;
  const sorted = [...values].sort((a, b) => a - b);
  const middleIndex = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middleIndex - 1] + sorted[middleIndex]) / 2 : sorted[middleIndex];
};

/**
 * @function computeStandardDeviation
 * @description Computes the sample standard deviation of a numeric array.
 * @param {Array<Number>} values - Values to compute the standard deviation of.
 * **Called by:
 * @see {detectOutlierShorelineDates}
 * @memberof module:ShoreR
 * @returns {Number} The sample standard deviation, or 0 if fewer than 2 values are given.
 */
const computeStandardDeviation = (values) => {
  if (values.length < 2) return 0;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
};

/**
 * @function shorelineDateToDaysSinceEpoch
 * @description Converts a shoreline date to whole days since the shared `EPOCH` reference date.
 * @param {String|Date} rawDate - Date to convert.
 * **Called by:
 * @see {detectOutlierShorelineDates}
 * @memberof module:ShoreR
 * @returns {Number} Days since `EPOCH`, or NaN if the date is missing/invalid.
 */
const shorelineDateToDaysSinceEpoch = (rawDate) => {
  if (rawDate == null) return NaN;
  const days = moment.utc(rawDate).diff(moment.utc(EPOCH), 'days');
  return Number.isFinite(days) ? days : NaN;
};

/**
 * @function formatShorelineLabel
 * @description Formats a shoreline date as "dd/mm/yyyy".
 * @param {String|Date} rawDate - Date to format.
 * **Called by:
 * @see {detectOutlierShorelineDates}
 * @memberof module:ShoreR
 * @returns {String} The formatted date label.
 */
const formatShorelineLabel = (rawDate) => {
  const iso = moment.utc(rawDate).format('YYYY-MM-DD');
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
};

/**
 * @function detectOutlierShorelineDates
 * @description Identifies shoreline dates whose distances behave as outliers across the transect
 * network.
 * 1. For each transect, computes the median/standard deviation of its regression distances and
 *    counts distances outside `median ± sigmaMultiplier*stdDev` as outliers, tallied per day.
 * 2. Flags a shoreline date when at least `ceil(totalTransects * transectRatioThreshold)`
 *    transects flagged that day as an outlier.
 * @param {Object|Array} transectData - GeoJSON FeatureCollection (or array) of transects.
 * @param {Object} shorelinesGeoJSON - GeoJSON FeatureCollection of shorelines.
 * @param {Object} [options]
 * @param {Number} [options.sigmaMultiplier=3] - Standard deviation multiplier for the outlier bounds.
 * @param {Number} [options.transectRatioThreshold=1/5] - Fraction of transects that must flag a date.
 * @see {@link computeMedian}
 * @see {@link computeStandardDeviation}
 * @see {@link shorelineDateToDaysSinceEpoch}
 * @see {@link formatShorelineLabel}
 * **Called by:
 * @see {ShoreRAnalysisSteps}
 * @memberof module:ShoreR
 * @returns {Array<{index: Number, date: *, label: String, timeStart: Number}>} Flagged shoreline dates.
 */
export const detectOutlierShorelineDates = (
  transectData,
  shorelinesGeoJSON,
  { sigmaMultiplier = 3, transectRatioThreshold = 1 / 5 } = {},
) => {
  const transectFeatures = Array.isArray(transectData) ? transectData : (transectData?.features ?? []);
  const shorelineFeatures = shorelinesGeoJSON?.features ?? [];
  if (!transectFeatures.length || !shorelineFeatures.length) return [];

  const totalTransects = transectFeatures.length;
  const minOutlierTransects = Math.ceil(totalTransects * transectRatioThreshold);

  const outlierHitsByDay = new Map();

  transectFeatures.forEach((transectFeature) => {
    const regressionEntries = transectFeature.properties?.[INTERNALS]?.regression ?? [];
    if (regressionEntries.length < 2) return;

    const distances = regressionEntries.map(([, distance]) => distance).filter(Number.isFinite);
    if (distances.length < 2) return;

    const median = computeMedian(distances);
    const standardDeviation = computeStandardDeviation(distances);
    if (standardDeviation === 0) return;

    const lowerBound = median - sigmaMultiplier * standardDeviation;
    const upperBound = median + sigmaMultiplier * standardDeviation;

    regressionEntries.forEach(([daysSinceEpoch, distance]) => {
      if (!Number.isFinite(distance)) return;
      if (distance < lowerBound || distance > upperBound) {
        const dayKey = Math.floor(daysSinceEpoch);
        outlierHitsByDay.set(dayKey, (outlierHitsByDay.get(dayKey) ?? 0) + 1);
      }
    });
  });

  const flagged = [];
  shorelineFeatures.forEach((shorelineFeature, index) => {
    const rawDate = shorelineFeature.properties?.date;
    const daysSinceEpoch = shorelineDateToDaysSinceEpoch(rawDate);
    if (!Number.isFinite(daysSinceEpoch)) return;

    const outlierTransectCount = outlierHitsByDay.get(daysSinceEpoch) ?? 0;
    if (outlierTransectCount >= minOutlierTransects) {
      const timeStart = Number(moment.utc(rawDate).valueOf());
      flagged.push({
        index,
        date: rawDate,
        label: formatShorelineLabel(rawDate),
        timeStart,
      });
    }
  });

  console.log(
    '[detect] totalTransects:',
    totalTransects,
    'minOutlierTransects:',
    minOutlierTransects,
    'hitsByDay:',
    [...outlierHitsByDay.entries()],
    'flagged:',
    flagged,
  );

  return flagged;
};

/**
 * @function getShorelineDates
 * @description Returns a date entry for each shoreline feature, used to populate the
 * reference-shoreline date selector, with labels formatted as "dd/mm/yyyy".
 * @param {Object} shorelinesGeoJSON - GeoJSON FeatureCollection of shorelines.
 * **Called by:
 * @see {ShoreRAnalysisSteps}
 * @memberof module:ShoreR
 * @returns {Array<{index: Number, date: String, label: String}>} One entry per shoreline feature.
 */
export const getShorelineDates = (shorelinesGeoJSON) => {
  const feats = shorelinesGeoJSON?.features;
  if (!feats?.length) return [];
  return feats.map((f, index) => {
    const date = f.properties?.date ?? '';
    let label = String(date);
    if (typeof date === 'string' && date.length >= 10) {
      const [y, m, d] = date.slice(0, 10).split('-');
      if (y && m && d) label = `${d}/${m}/${y}`;
    }
    return { index, date, label };
  });
};
